enum SELECTORS {
  ROOT_COMPONENT = "[data-component='side-slide']",
  TRIGGER_BUTTON = "[data-side-slide-trigger-id]",
  CLOSE_BUTTON = "[data-side-slide-close-button]",
  TITLE = "[data-custom-selector='aria-labelledby']",
  DESCRIPTION = "[data-custom-selector='aria-describedby']",
}

enum ATTRIBUTES {
  SLIDE_ID = "data-side-slide-id",
  TRIGGER_ID = "data-side-slide-trigger-id",
  TARGET_SLIDE_ID = "data-trigger-target",
  OPEN_STATE = "data-open",
  SLIDE_STATE = "data-slide-in",
}

export type UniqueIds = {
  sliderId: string;
  titleId: string;
  descriptionId: string;
};

export class SideSlideManager {
  private triggerElements: HTMLElement[] = [];
  private backgroundClickHandlerMap = new WeakMap<
    HTMLElement,
    (event: Event) => void
  >();

  constructor() {
    window.addEventListener("DOMContentLoaded", this.initialize.bind(this));
  }

  private initialize(): void {
    const slideElements = Array.from(
      document.querySelectorAll<HTMLElement>(SELECTORS.ROOT_COMPONENT),
    );
    if (slideElements.length === 0) return;

    slideElements.forEach((slide) => this.registerSlide(slide));

    this.triggerElements = Array.from(
      document.querySelectorAll<HTMLElement>(SELECTORS.TRIGGER_BUTTON),
    );

    if (this.triggerElements.length === 0) {
      console.warn("No Trigger Buttons found.");
      return;
    }

    this.addTriggerEventListeners();
    this.addCloseButtonEventListeners();
    this.addKeyboardEventListeners();
  }

  private addKeyboardEventListeners(): void {
    document.addEventListener("keydown", (event) => this.handleKeyDown(event));
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      const openSlide = this.getOpenSlide();
      if (openSlide) {
        this.closeSlide(openSlide);
      }
    }
  }

  private getOpenSlide(): HTMLElement | undefined {
    const slideElements = Array.from(
      document.querySelectorAll<HTMLElement>(SELECTORS.ROOT_COMPONENT),
    );
    if (slideElements.length === 0) return;
    return slideElements.find(
      (slide) => slide.getAttribute(ATTRIBUTES.OPEN_STATE) === "true",
    );
  }

  private generateUniqueIdsObject(): UniqueIds {
    const sliderId = crypto.randomUUID();
    const titleId = crypto.randomUUID();
    const descriptionId = crypto.randomUUID();
    return { sliderId, titleId, descriptionId };
  }

  private registerSlide(slide: HTMLElement): void {
    const idsObject = this.generateUniqueIdsObject();
    slide.setAttribute(ATTRIBUTES.SLIDE_ID, idsObject.sliderId);
    this.addIdToTitle(slide, idsObject);
    this.addIdToDescription(slide, idsObject);
    slide
      .querySelector("aside")
      ?.setAttribute("aria-labelledby", idsObject.titleId);
    slide
      .querySelector("aside")
      ?.setAttribute("aria-describedby", idsObject.descriptionId);
  }

  private addIdToTitle(slide: HTMLElement, ids: UniqueIds) {
    const title = slide.querySelector(SELECTORS.TITLE);
    if (!(title instanceof HTMLElement)) return;
    title.setAttribute("id", ids.titleId);
  }

  private addIdToDescription(slide: HTMLElement, ids: UniqueIds) {
    const description = slide.querySelector(SELECTORS.DESCRIPTION);
    if (!(description instanceof HTMLElement)) return;
    description.setAttribute("id", ids.descriptionId);
  }

  private addTriggerEventListeners(): void {
    this.triggerElements.forEach((trigger) => {
      trigger.addEventListener("click", (event) =>
        this.handleTriggerClick(event),
      );
    });
  }

  private handleTriggerClick(event: MouseEvent): void {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const triggerId = trigger.getAttribute(ATTRIBUTES.TRIGGER_ID);
    if (!triggerId) return;

    const associatedSlide = this.getSlideByTriggerId(triggerId);
    if (associatedSlide) {
      this.toggleSlideState(associatedSlide);
    }
  }

  private toggleSlideState(slide: HTMLElement): void {
    const isOpen = slide.getAttribute(ATTRIBUTES.OPEN_STATE) === "true";
    if (isOpen) {
      this.closeSlide(slide);
    } else {
      this.openSlide(slide);
    }
  }

  private openSlide(slide: HTMLElement): void {
    slide.setAttribute(ATTRIBUTES.OPEN_STATE, "true");
    slide.setAttribute(ATTRIBUTES.SLIDE_STATE, "true");
    this.disablePageScrolling();
    const closeButton = slide.querySelector("[data-side-slide-close-button]");

    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }

    const handler = (event: Event) =>
      this.handleSlideBackgroundClick(event, slide);
    slide.addEventListener("click", handler);
    this.backgroundClickHandlerMap.set(slide, handler);
  }

  private closeSlide(slide: HTMLElement): void {
    slide.setAttribute(ATTRIBUTES.OPEN_STATE, "false");
    slide.setAttribute(ATTRIBUTES.SLIDE_STATE, "false");
    this.enablePageScrolling();
    const handler = this.backgroundClickHandlerMap.get(slide);
    if (!handler) return;
    slide.removeEventListener("click", handler);
    this.backgroundClickHandlerMap.delete(slide);
  }

  private disablePageScrolling() {
    const scrollbarWidth = 10;
    document.body?.style.setProperty("overflow", "hidden", "important");
    document.body?.style.setProperty(
      "padding-right",
      `${scrollbarWidth}px`,
      "important",
    );
    document.body?.style.setProperty("max-height", "100dvh", "important");
  }

  private enablePageScrolling() {
    document.body?.style.removeProperty("overflow");
    document.body?.style.removeProperty("padding-right");
    document.body?.style.removeProperty("max-height");
  }

  private handleSlideBackgroundClick(event: Event, slide: HTMLElement): void {
    const insideContentArea = slide.querySelector("aside");
    const clickTarget = event.target as Node;

    if (insideContentArea && !insideContentArea.contains(clickTarget)) {
      this.closeSlide(slide);
    }
  }

  private addCloseButtonEventListeners(): void {
    const closeButtons = document.querySelectorAll<HTMLElement>(
      SELECTORS.CLOSE_BUTTON,
    );
    if (closeButtons.length === 0) {
      console.warn("No Close Buttons found.");
      return;
    }

    closeButtons.forEach((button) => {
      button.addEventListener("click", () =>
        this.handleCloseButtonClick(button),
      );
    });
  }

  private handleCloseButtonClick(closeButton: Element): void {
    const slideId = closeButton.getAttribute(ATTRIBUTES.TARGET_SLIDE_ID);
    if (!slideId) return;

    const slide = this.getSlideByTriggerId(slideId);
    if (slide) this.closeSlide(slide);
  }

  private getSlideByTriggerId(triggerId: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `[${ATTRIBUTES.TARGET_SLIDE_ID}="${triggerId}"]`,
    );
  }
}
new SideSlideManager();
